<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>
  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>

  <div v-if="showToast" class="toast-overlay">
    <div class="toast-message">Innstillingene er lagret</div>
  </div>

  <div class="container mt-4">
    <div style="max-width:597px">
        <h3>Rediger utsalgssted</h3>
        <br/>

        <div class="section">
          <h5 class="section-title">Generelt</h5>

          <div>
            <span class="ingredient-title">Navn</span>
            <input class="form-control" type="text" v-model="location.name" />
          </div>

          <div>
            <span class="ingredient-title">Visningsnavn</span>
            <input class="form-control" type="text" v-model="location.displayName" :placeholder="location.name" />
            <div style="color:var(--bo-muted); font-size:0.85rem" class="mt-1">
              Navnet gjestene ser: øverst i nettbutikken, på bordbookingen og i bekreftelsene de får. Tomt: Navn brukes.
            </div>
          </div>

          <div>
            <span class="ingredient-title">Forkortet Navn</span>
            <input class="form-control" type="text" v-model="location.shortName" />
          </div>

          <div>
            <span class="bo_label">Header bilde</span>
            <input type="file" class="form-control" accept="image/*" ref="headerFile" @change="selectHeaderFile" />
            <button type='button' class='bo_btn mt-2' @click='uploadHeaderFile' :disabled="!activateUploadHeaderFileButton"> Last opp </button>
          </div>

          <div>
            <span class="ingredient-title">Google Maps Iframe Url</span>
            <input class="form-control" type="text" v-model="location.googleMapsIframeUrl" />
          </div>

          <div>
            <span class="ingredient-title">Organisasjonsnummer</span>
            <input class="form-control" type="text" v-model="location.vatNumber" />
          </div>

          <div>
            <span class="ingredient-title">Gate</span>
            <input class="form-control" type="text" v-model="location.street" />
          </div>

          <div>
            <span class="ingredient-title">Postnummer</span>
            <input class="form-control" type="number" v-model="location.zipCode" />
          </div>

          <div>
            <span class="ingredient-title">Poststed</span>
            <input class="form-control" type="text" v-model="location.city" />
          </div>

          <div>
            <span class="ingredient-title">Telefon</span>
            <input class="form-control" type="number" v-model="location.phone" />
          </div>

          <div>
            <span class="ingredient-title">Epost</span>
            <input class="form-control" type="email" v-model="location.email" @input="validateEmail" />
          </div>
          <div v-if="!validEmail">
            <span class="validation-failed">(Ugyldig epost)</span>
          </div>

          <div v-if="isSuperAdmin()" class="mt-4">
            <span class="ingredient-title">PSP</span>
            <select class="form-select" v-model="location.onlinePaymentProvider">
              <option value="NOT_ANY">Ingen</option>
              <option value="VIVA">Viva</option>
              <option value="DINTERO">Dintero</option>
              <option value="SWEDBANK_PAY">Swedbank Pay</option>
            </select>
          </div>

          <div v-if="isSuperAdmin()" class="mt-4">
            <span class="ingredient-title">Regnskapssystem</span>
            <select class="form-select" v-model="location.accountingSystem">
              <option value="NONE">Ingen</option>
              <option value="UNIMICRO">Unimicro</option>
              <option value="TRIPLETEX">Tripletex</option>
              <option value="FIKEN">Fiken</option>
            </select>
          </div>

          <!-- location.useOrderDisplay: the flag for an order display, which existed before any display did. It decides
               whether guests GET a short number — the till prints it large, the kiosk shows and texts it, web orders
               get one in COMMON mode — and so whether the kundeklarskjerm (TastyODS) has a number to show. -->
          <div class="mt-4" style="clear: both;">
            <div class="form-switch-wrapper mb-2">
              <label class="form-switch">
                <div class="form-switch-container">
                  <span class="bold me-2">Bestillingsnummer for gjestene</span>
                  <input type="checkbox" v-model="location.useOrderDisplay" />
                  <i></i>
                </div>
              </label>
            </div>
            <small class="text-muted" style="clear: both; display: block;">
              Gjesten får et kort nummer: stort på kvitteringen, i kiosken og på SMS. Det er nummeret kundeklarskjermen viser.
              Med «Felles for alle kanaler» under får også nettbestillinger et nummer.
            </small>
          </div>

          <div class="mt-4">
            <span class="ingredient-title">Bestillingsnr-beregning (present counter)</span>
            <select class="form-select" v-model="location.presentCounter.mode">
              <option value="TERMINAL">Terminal-basert (offline)</option>
              <option value="COMMON">Felles for alle kanaler (online)</option>
            </select>
            <div style="color:var(--bo-muted); font-size:0.85rem" class="mt-1">
              <strong>Terminal-basert (offline):</strong> hver terminal teller selv og fungerer uten nett — nummeret er
              terminal-ID + løpenummer (f.eks. «10, 11 … 19, 110» på terminal 1, «20, 21 …» på terminal 2).<br />
              <strong>Felles for alle kanaler (online):</strong> én løpende serie per lokasjon delt av alle terminaler,
              kiosker og online-bestillinger (1, 2, 3 …). Krever nettforbindelse.
            </div>

            <div v-if="location.presentCounter.mode === 'COMMON'" class="row g-2 mt-2">
              <div class="col-6">
                <label class="form-label mb-1" style="font-size:0.85rem; color:var(--bo-muted)">Start-nr</label>
                <input class="form-control" type="number" min="1" v-model.number="location.presentCounter.startCounter" placeholder="1" />
              </div>
              <div class="col-6">
                <label class="form-label mb-1" style="font-size:0.85rem; color:var(--bo-muted)">Maks-nr (0 = ingen grense)</label>
                <input class="form-control" type="number" min="0" v-model.number="location.presentCounter.maxCounter" placeholder="0" />
              </div>
              <div class="col-12" style="color:var(--bo-muted); font-size:0.85rem">
                Serien starter på <strong>start-nr</strong> og teller oppover; når den passerer <strong>maks-nr</strong>
                begynner den på start-nr igjen. Maks-nr = 0 betyr ingen grense. (Gjelder kun felles/online-modus.)
              </div>
            </div>

            <div v-if="isSuperAdmin()" class="mt-3">
              <button type="button" class="bo_btn_secondary" @click="confirmResetPresentCounter">Nullstill bestillingsnr-serien</button>
              <span v-if="resetMessage" :class="resetError ? 'text-danger' : 'text-success'" class="ms-2" style="font-size:0.85rem">{{ resetMessage }}</span>
              <div style="color:var(--bo-muted); font-size:0.82rem" class="mt-1">
                Nullstiller den løpende serien for denne lokasjonen nå (samme som skjer ved dagens oppgjør) —
                neste bestilling starter på nytt. Felles/online: fra start-nr. Terminal-basert: hver terminal fra 1.
              </div>
            </div>
          </div>

          <span class="ingredient-title mt-4" style="display: block;">Andre</span>
          <div class="subsection">
            <label style="font-weight: bold;">
              <input type="checkbox" class="multiple-checkbox" v-model="location.offerQR" /> Tilbyr QR-bestilling (offerQR)
            </label>
            <div class="mt-3">
            <label style="font-weight: bold;">
              <input type="checkbox" class="multiple-checkbox" v-model="location.offerDeliveryService" /> Tilbyr levering (offerDeliveryService)
            </label>
            </div>
            <div class="mt-3">
            <label style="font-weight: bold;">
              <input type="checkbox" class="multiple-checkbox" v-model="location.skipOrderTypeSelection" /> Hopp over bestillingstype-valg (skipOrderTypeSelection)
            </label>
            </div>
            <div class="mt-3">
            <label style="font-weight: bold;">
              <input type="checkbox" class="multiple-checkbox" v-model="location.useManualPickupOrDeliveryTime" /> Bruk manuell hentetid – deaktiverer smart hentetid (useManualPickupOrDeliveryTime)
            </label>
            </div>
            <div class="mt-3">
            <label style="font-weight: bold;">
              <input type="checkbox" class="multiple-checkbox" v-model="location.showPickupTimeOptions" /> Vis valg for hentetidspunkt – la gjest velge tidspunkt (showPickupTimeOptions)
            </label>
            </div>

          </div>
        </div>

        <div v-if="isSuperAdmin()" class="section mt-4">
          <h5 class="section-title">Kontaktpersoner</h5>
          <p class="text-muted" style="font-size: 0.9rem;">Mottar SMS-varsel ved f.eks. nedetid på betalingsløsning.</p>

          <div class="subsection">
            <div v-for="(contact, index) in location.contactPersons" :key="index" class="mb-3">
              <div class="row g-2 align-items-end">
                <div class="col-sm-4">
                  <span class="ingredient-title">Navn</span>
                  <input class="form-control" type="text" v-model="contact.name" />
                </div>
                <div class="col-sm-3">
                  <span class="ingredient-title">Mobilnummer</span>
                  <input class="form-control" type="text" v-model="contact.phone" />
                </div>
                <div class="col-sm-3">
                  <span class="ingredient-title">Rolle</span>
                  <input class="form-control" type="text" v-model="contact.role" placeholder="(valgfritt)" />
                </div>
                <div class="col-sm-2">
                  <button type="button" class="btn btn-sm btn-outline-danger" @click="removeContactPerson(index)">Fjern</button>
                </div>
              </div>
            </div>

            <button type="button" class="bo_btn mt-2" @click="addContactPerson"> Legg til kontaktperson </button>
          </div>
        </div>

        <div class="section mt-4">
          <h5 class="section-title">POS innstillinger</h5>

          <div class="subsection">
            <h6 class="subsection-title">Bestillingstyper</h6>

            <fieldset>
              <label class="multiple-checkbox-label">
                <input type="checkbox" class="multiple-checkbox" v-model="posOrderTypeDineIn" /> Spise her
              </label>
              <label class="multiple-checkbox-label">
                <input type="checkbox" class="multiple-checkbox" v-model="posOrderTypePickup" /> Ta med
              </label>
            </fieldset>
          </div>

          <div class="subsection mt-3">
            <h6 class="subsection-title">Knapper</h6>

            <div class="mt-2">
              <span class="ingredient-title">Knapp farge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.posSetting.colorButton || '#fc811d'" @input="location.posSetting.colorButton = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.posSetting.colorButton)}" type="text" v-model="location.posSetting.colorButton" placeholder="#fc811d" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.posSetting.colorButton = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.posSetting.colorButton)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #fc811d)</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Knapp trykket farge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.posSetting.colorButtonPressed || '#dd143b'" @input="location.posSetting.colorButtonPressed = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.posSetting.colorButtonPressed)}" type="text" v-model="location.posSetting.colorButtonPressed" placeholder="#dd143b" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.posSetting.colorButtonPressed = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.posSetting.colorButtonPressed)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #dd143b)</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Knapp deaktivert farge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.posSetting.colorButtonDisabled || '#808080'" @input="location.posSetting.colorButtonDisabled = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.posSetting.colorButtonDisabled)}" type="text" v-model="location.posSetting.colorButtonDisabled" placeholder="#808080" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.posSetting.colorButtonDisabled = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.posSetting.colorButtonDisabled)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #808080)</small>
            </div>
          </div>
        </div>

        <div class="section mt-4">
          <h5 class="section-title">Kiosk innstillinger</h5>

          <div class="subsection">
            <h6 class="subsection-title">Bestillingstyper</h6>

            <fieldset>
              <label class="multiple-checkbox-label">
                <input type="checkbox" class="multiple-checkbox" v-model="serviceOptionDineIn" /> Spise her
              </label>
              <label class="multiple-checkbox-label">
                <input type="checkbox" class="multiple-checkbox" v-model="serviceOptionPickup" /> Ta med
              </label>
            </fieldset>
            <p class="bo_form-hint">Ingen avkrysset betyr at kiosken tilbyr begge.</p>
          </div>

          <div style="clear: both;">
            <div class="form-switch-wrapper mt-3 mb-4">
              <label class="form-switch">
                <div class="form-switch-container">
                  <span class="bold me-2">Vis header</span>
                  <input type="checkbox" v-model="location.kioskSetting.showHeader" />
                  <i></i>
                </div>
              </label>
            </div>
            <small v-if="location.kioskSetting.showHeader" class="text-muted" style="clear: both; display: block;">Vises på: Hovedside, Tilvalg, Undervalg, Betaling</small>
          </div>

          <div class="mt-3" style="clear: both;">
            <span class="ingredient-title">Spise her knapp-tekst</span>
            <input class="form-control" type="text" v-model="location.kioskSetting.dineInLabel" placeholder="Spise her" />
          </div>

          <div>
            <span class="ingredient-title">Ta med knapp-tekst</span>
            <input class="form-control" type="text" v-model="location.kioskSetting.pickupLabel" placeholder="Ta med" />
          </div>

          <div class="mt-3">
            <span class="ingredient-title">Knappestørrelse startside</span>
            <select class="form-select" v-model="location.kioskSetting.startPageButtonSize">
              <option value="SMALL">Liten</option>
              <option value="MEDIUM">Medium</option>
              <option value="LARGE">Stor</option>
              <option value="EXTRA_LARGE">Ekstra stor</option>
            </select>
          </div>

          <div class="subsection mt-3">
            <h6 class="subsection-title">Knapper</h6>

            <div class="mt-2">
              <span class="ingredient-title">Knapp farge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorButton || '#fc811d'" @input="location.kioskSetting.colorButton = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorButton)}" type="text" v-model="location.kioskSetting.colorButton" placeholder="#fc811d" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorButton = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorButton)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #fc811d)</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Knapp trykket farge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorButtonPressed || '#dd143b'" @input="location.kioskSetting.colorButtonPressed = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorButtonPressed)}" type="text" v-model="location.kioskSetting.colorButtonPressed" placeholder="#dd143b" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorButtonPressed = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorButtonPressed)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #dd143b)</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Knapp deaktivert farge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorButtonDisabled || '#808080'" @input="location.kioskSetting.colorButtonDisabled = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorButtonDisabled)}" type="text" v-model="location.kioskSetting.colorButtonDisabled" placeholder="#808080" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorButtonDisabled = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorButtonDisabled)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #808080)</small>
            </div>
          </div>

          <div class="subsection mt-3">
            <h6 class="subsection-title">Kategorier</h6>

            <div class="mt-2">
              <span class="ingredient-title">Kategorier bakgrunnsfarge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorCategoriesPanelBg || '#606060'" @input="location.kioskSetting.colorCategoriesPanelBg = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorCategoriesPanelBg)}" type="text" v-model="location.kioskSetting.colorCategoriesPanelBg" placeholder="#606060" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorCategoriesPanelBg = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorCategoriesPanelBg)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #606060)</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Kategori celle bakgrunnsfarge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorCategoryCellBg || '#606060'" @input="location.kioskSetting.colorCategoryCellBg = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorCategoryCellBg)}" type="text" v-model="location.kioskSetting.colorCategoryCellBg" placeholder="#606060" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorCategoryCellBg = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorCategoryCellBg)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #606060)</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Kategori celle valgt bakgrunnsfarge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorCategoryCellSelectedBg || '#999999'" @input="location.kioskSetting.colorCategoryCellSelectedBg = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorCategoryCellSelectedBg)}" type="text" v-model="location.kioskSetting.colorCategoryCellSelectedBg" placeholder="#999999" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorCategoryCellSelectedBg = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorCategoryCellSelectedBg)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #999999)</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Kategori tittel tekstfarge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorCategoryTitleText || 'var(--bo-surface)'" @input="location.kioskSetting.colorCategoryTitleText = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorCategoryTitleText)}" type="text" v-model="location.kioskSetting.colorCategoryTitleText" placeholder="var(--bo-surface)" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorCategoryTitleText = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorCategoryTitleText)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. var(--bo-surface))</small>
            </div>
          </div>

          <div class="subsection mt-3">
            <h6 class="subsection-title">Produkter</h6>

            <div class="mt-2">
              <span class="ingredient-title">Produkter bakgrunnsfarge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorItemsPanelBg || '#999999'" @input="location.kioskSetting.colorItemsPanelBg = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorItemsPanelBg)}" type="text" v-model="location.kioskSetting.colorItemsPanelBg" placeholder="#999999" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorItemsPanelBg = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorItemsPanelBg)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #999999)</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Produkt celle bakgrunnsfarge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorItemCellBg || '#606060'" @input="location.kioskSetting.colorItemCellBg = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorItemCellBg)}" type="text" v-model="location.kioskSetting.colorItemCellBg" placeholder="#606060" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorItemCellBg = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorItemCellBg)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #606060)</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Produktnavn tekstfarge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorItemNameText || 'var(--bo-surface)'" @input="location.kioskSetting.colorItemNameText = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorItemNameText)}" type="text" v-model="location.kioskSetting.colorItemNameText" placeholder="var(--bo-surface)" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorItemNameText = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorItemNameText)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. var(--bo-surface))</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Produktbeskrivelse tekstfarge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorItemDescriptionText || 'var(--bo-surface)'" @input="location.kioskSetting.colorItemDescriptionText = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorItemDescriptionText)}" type="text" v-model="location.kioskSetting.colorItemDescriptionText" placeholder="var(--bo-surface)" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorItemDescriptionText = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorItemDescriptionText)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. var(--bo-surface))</small>
            </div>

            <div class="mt-3">
              <span class="ingredient-title">Produktpris tekstfarge</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kioskSetting.colorItemPriceText || '#fc811d'" @input="location.kioskSetting.colorItemPriceText = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kioskSetting.colorItemPriceText)}" type="text" v-model="location.kioskSetting.colorItemPriceText" placeholder="#fc811d" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kioskSetting.colorItemPriceText = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kioskSetting.colorItemPriceText)" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. #fc811d)</small>
            </div>
          </div>
        </div>

        <!-- The kitchen display (location.kdsSetting): which card it shows, its ticket timer, its colours. An emptied
             field is not saved, and the KDS then uses its own default — the placeholder shows which. -->
        <div class="section mt-4">
          <h5 class="section-title">KDS innstillinger</h5>
          <p class="bo_form-hint mt-0">Hvordan kjøkkenskjermen viser bestillingene. Et tomt felt bruker skjermens standard.</p>

          <div class="subsection">
            <h6 class="subsection-title">Kortet</h6>
            <div style="clear: both;">
              <div class="form-switch-wrapper mt-2 mb-2">
                <label class="form-switch">
                  <div class="form-switch-container">
                    <span class="bold me-2">Kjøkkenkort</span>
                    <input type="checkbox" v-model="location.kdsSetting.kitchenCard" />
                    <i></i>
                  </div>
                </label>
              </div>
              <small class="text-muted" style="clear: both; display: block;">
                {{ location.kdsSetting.kitchenCard
                  ? 'Bestillingsnummer, bord og hvor bestillingen kom fra vises stort. Priser, total og telefonnummer vises ikke.'
                  : 'Kortet som før: ordrenummer, total, navn og telefonnummer.' }}
              </small>
            </div>
          </div>

          <div class="subsection mt-3">
            <h6 class="subsection-title">Maten klar-SMS</h6>
            <div style="clear: both;">
              <div class="form-switch-wrapper mt-2 mb-2">
                <label class="form-switch">
                  <div class="form-switch-container">
                    <span class="bold me-2">Send automatisk når kjøkkenet trykker Ferdig</span>
                    <input type="checkbox" v-model="location.kdsSetting.autoFoodReadySms" />
                    <i></i>
                  </div>
                </label>
              </div>
              <small class="text-muted" style="clear: both; display: block;">
                {{ location.kdsSetting.autoFoodReadySms
                  ? 'Gjester som har valgt «når maten er klar» får SMS med en gang kjøkkenet trykker Ferdig, med standardteksten. Hver SMS koster kr 1.'
                  : 'Kjøkkenet trykker «Send maten klar SMS» og kan endre teksten før den sendes, som i dag.' }}
              </small>
            </div>
          </div>

          <div class="subsection mt-3">
            <h6 class="subsection-title">Timer</h6>
            <div class="row g-2 mt-1">
              <div v-for="field in kdsMinuteFields" :key="field.key" class="col-sm-6">
                <span class="ingredient-title">{{ field.label }}</span>
                <input class="form-control" :class="{'is-invalid': !isValidMinutes(location.kdsSetting[field.key])}" type="number" min="1" :max="maxKdsMinutes" step="1" v-model.number="location.kdsSetting[field.key]" :placeholder="field.placeholder" />
                <small v-if="!isValidMinutes(location.kdsSetting[field.key])" class="text-danger">Skriv et helt antall minutter (1–{{ maxKdsMinutes }}), eller la feltet stå tomt.</small>
                <small v-else class="text-muted" style="display: block;">{{ field.hint }}</small>
              </div>
            </div>
          </div>

          <div class="subsection mt-3">
            <h6 class="subsection-title">Visning</h6>
            <!-- Dark screen and text size are chosen on each screen (TastyKDS, 2026-10-09), not here. -->
            <p class="bo_form-hint mt-0">Mørk skjerm og tekststørrelse velges på hver kjøkkenskjerm, med knappene øverst til høyre.</p>
            <div class="row g-2 mt-2">
              <div class="col-sm-6">
                <span class="ingredient-title">Rekkefølge</span>
                <select class="form-select" v-model="location.kdsSetting.sortBy">
                  <option value="ORDERED">Etter når bestillingen kom</option>
                  <option value="DUE">Etter når den skal være ferdig</option>
                </select>
              </div>
            </div>
          </div>

          <!-- TastyKDS kitchen/Stations: each screen picks its station under «Skjerm» on the screen itself. -->
          <div class="subsection mt-3">
            <h6 class="subsection-title">Stasjoner</h6>
            <p class="bo_form-hint mt-0">Del kjøkkenet i stasjoner, for eksempel Grill, Kald og Bar. Hver kjøkkenskjerm velger selv hvilken stasjon den er, under «Skjerm» på skjermen. Kategorier uten stasjon vises på alle stasjonene. Uten stasjoner viser alle skjermene alle linjer, som før.</p>

            <div v-for="(station, index) in location.kdsSetting.stations" :key="station.id" class="mt-3">
              <div class="d-flex align-items-center gap-2">
                <button type="button" class="bo_btn_secondary" @click="removeKdsStation(index)"><i class="fas fa-times"></i> Fjern</button>
                <input class="form-control" :class="{'is-invalid': !station.name.trim()}" type="text" v-model="station.name" placeholder="Navn, f.eks. Grill" />
              </div>
              <small v-if="!station.name.trim()" class="text-danger">Gi stasjonen et navn.</small>
              <fieldset class="mt-2">
                <label v-for="category in kdsStationCategories" :key="category.id" class="multiple-checkbox-label">
                  <input type="checkbox" class="multiple-checkbox" :value="category.id" v-model="station.categoryIds" /> {{ category.name }}
                </label>
              </fieldset>
              <small v-if="station.categoryIds.length === 0" class="text-muted" style="display: block;">Ingen kategorier valgt: stasjonen får bare kategoriene som ikke har noen stasjon.</small>
            </div>

            <button type="button" class="bo_btn_secondary mt-3" @click="addKdsStation">Legg til stasjon</button>

            <div v-if="location.kdsSetting.stations.length > 0" style="clear: both;">
              <div class="form-switch-wrapper mt-3 mb-2">
                <label class="form-switch">
                  <div class="form-switch-container">
                    <span class="bold me-2">Ekspo-skjerm på passet</span>
                    <input type="checkbox" v-model="location.kdsSetting.useExpo" />
                    <i></i>
                  </div>
                </label>
              </div>
              <small class="text-muted" style="clear: both; display: block;">
                {{ location.kdsSetting.useExpo
                  ? 'En skjerm satt opp som Ekspo ser alle bestillingene med fremdrift per stasjon, og trykker Ferdig når alt er klart til pass.'
                  : 'Uten ekspo blir en bestilling ferdig av seg selv når alle stasjonene den venter på er ferdige. Bestillinger der gjesten får «maten klar»-SMS kommer tilbake på stasjonene til noen sender meldingen.' }}
              </small>
            </div>
          </div>

          <div v-for="group in kdsColorGroups" :key="group.title" class="subsection mt-3">
            <h6 class="subsection-title">{{ group.title }}</h6>

            <div v-for="(field, fieldIndex) in group.fields" :key="field.key" :class="fieldIndex === 0 ? 'mt-2' : 'mt-3'">
              <span class="ingredient-title">{{ field.label }}</span>
              <div class="d-flex align-items-center gap-2">
                <input type="color" :value="location.kdsSetting[field.key] || field.placeholder" @input="location.kdsSetting[field.key] = $event.target.value" style="width:50px;height:38px;padding:2px;" />
                <input class="form-control" :class="{'is-invalid': !isValidColor(location.kdsSetting[field.key])}" type="text" v-model="location.kdsSetting[field.key]" :placeholder="field.placeholder" />
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="location.kdsSetting[field.key] = ''">Tilbakestill</button>
              </div>
              <small v-if="!isValidColor(location.kdsSetting[field.key])" class="text-danger">Ugyldig fargekode. Bruk format #RRGGBB (f.eks. {{ field.placeholder }})</small>
            </div>
          </div>
        </div>

      <div class="sticky-footer">
        <button v-if="activateSaveButton" type="button" class="bo_btn" @click="saveLocation"> Lagre </button>
        <button v-else type="button" class="bo_btn" @click="saveLocation" disabled="true"> Lagre </button>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
import UserUtil from "@/util/UserUtil";
import LocationService from "@/services/location/LocationService";
import MediaFileService from "@/services/media/MediaFileService";
import ProductService from "@/services/product/ProductService";

/**
 * The kitchen display's colours, as TastyKDS reads them from location.kdsSetting. One list for the form, the
 * validation and the save. The placeholder is the KDS's own layout colour — what it uses when the field is left
 * empty. The buttons are the two on every order card (Hentet, Ferdig/Send SMS): the same keys posSetting uses on
 * the till, and onboarding seeds them with the till's colours.
 */
const KDS_COLOR_GROUPS = [
  { title: 'Knapper', fields: [
    { key: 'colorButton', label: 'Knapp farge', placeholder: '#fc811d' },
    { key: 'colorButtonPressed', label: 'Knapp trykket farge', placeholder: '#dd143b' },
    { key: 'colorButtonDisabled', label: 'Knapp deaktivert farge', placeholder: '#808080' }
  ]},
  { title: 'Spise her', fields: [
    { key: 'dineInBackgroundColor', label: 'Kortets bakgrunn', placeholder: '#ffffff' },
    { key: 'dineInHeaderBackgroundColor', label: 'Toppfeltets bakgrunn', placeholder: '#606060' },
    { key: 'dineInOrderTypeColor', label: 'Bestillingstype', placeholder: '#fc811d' },
    { key: 'dineInPickupOrDeliveryTimeColor', label: 'Hentetid', placeholder: '#fc811d' }
  ]},
  { title: 'Ta med', fields: [
    { key: 'pickupBackgroundColor', label: 'Kortets bakgrunn', placeholder: '#ffffff' },
    { key: 'pickupHeaderBackgroundColor', label: 'Toppfeltets bakgrunn', placeholder: '#606060' },
    { key: 'pickupOrderTypeColor', label: 'Bestillingstype', placeholder: '#fc811d' },
    { key: 'pickupPickupOrDeliveryTimeColor', label: 'Hentetid', placeholder: '#fc811d' }
  ]},
  { title: 'Tekst', fields: [
    { key: 'headerTextColor', label: 'Tekst i toppfeltet', placeholder: '#ffffff' },
    { key: 'orderDescriptionColor', label: 'Kommentar', placeholder: '#000000' }
  ]}
];
const KDS_COLOR_FIELDS = KDS_COLOR_GROUPS.flatMap(group => group.fields.map(field => field.key));

/**
 * The KDS ticket timer (TastyKDS kitchen/KdsSettings). Empty = not saved = the KDS's own default, which is the
 * placeholder. The timer is green, turns yellow `timerWarningMinutes` before the ticket is due and red when it is
 * late; an order is due at the time promised to the guest, else when it was ordered plus its slowest item's
 * prepTimeMinutes — or `defaultPrepMinutes` when no item has one.
 */
const KDS_MINUTE_FIELDS = [
  { key: 'defaultPrepMinutes', label: 'Tilberedningstid (min)', placeholder: '15',
    hint: 'Brukes for bestillinger der ingen vare har egen tilberedningstid.' },
  { key: 'timerWarningMinutes', label: 'Gul før forfall (min)', placeholder: '5',
    hint: 'Timeren blir gul så mange minutter før bongen skal være ferdig, og rød når tiden er ute.' }
];
const MAX_KDS_MINUTES = 240;

/** The KDS's sort order (TastyKDS kitchen/KdsSettings): the first is the default, stored as absent. */
const KDS_SORT_ORDERS = ['ORDERED', 'DUE'];

export default {
  components: {LoadingSpinner, ConfirmDialogue},
  data() {
    return {
      loading: false,
      showToast: false,
      kdsColorGroups: KDS_COLOR_GROUPS,
      kdsMinuteFields: KDS_MINUTE_FIELDS,
      kdsStationCategories: [],
      maxKdsMinutes: MAX_KDS_MINUTES,

      locationId: '',
      validEmail: true,
      resetMessage: '',
      resetError: false,
      location: {
        type: "location",
        merchant: "",
        name: "",
        shortName: "",
        headerImageFile: "",
        googleMapsIframeUrl: "",
        vatNumber: "",
        street: "",
        zipCode: "",
        city: "",
        phone: "",
        email: "",
        onlinePaymentProvider: "NOT_ANY",
        accountingSystem: "NONE",
        presentCounter: { mode: "TERMINAL", startCounter: 1, maxCounter: 0 },
        offerQR: false,
        offerDeliveryService: false,
        skipOrderTypeSelection: false,
        posSetting: {
          serviceOptions: [],
          colorButton: '',
          colorButtonPressed: '',
          colorButtonDisabled: ''
        },
        status: "ACTIVE",
        kioskSetting: {
          dineInLabel: '',
          pickupLabel: '',
          colorButton: '',
          colorButtonPressed: '',
          colorButtonDisabled: '',
          colorCategoriesPanelBg: '',
          colorCategoryCellBg: '',
          colorCategoryCellSelectedBg: '',
          colorCategoryTitleText: '',
          colorItemsPanelBg: '',
          colorItemCellBg: '',
          colorItemNameText: '',
          colorItemDescriptionText: '',
          colorItemPriceText: '',
          serviceOptions: [],
          startPageButtonSize: 'MEDIUM'
        },
        kdsSetting: {
          ...Object.fromEntries(KDS_COLOR_FIELDS.map(field => [field, ''])),
          ...Object.fromEntries(KDS_MINUTE_FIELDS.map(field => [field.key, ''])),
          kitchenCard: false,
          sortBy: 'ORDERED',
          stations: [],
          useExpo: false,
          autoFoodReadySms: false
        }
      }
    };
  },
  mounted() {
    this.locationId = this.$route.query.locationId;
    LocationService.getLocation(this.locationId).then(
        (response) => {
          this.location = response.data;
          if(this.location.useOrderDisplay === undefined || this.location.useOrderDisplay === null){
            this.location.useOrderDisplay = false;
          }
          if(this.location.offerQR === undefined || this.location.offerQR === null){
            this.location.offerQR = false;
          }
          if(this.location.offerDeliveryService === undefined || this.location.offerDeliveryService === null){
            this.location.offerDeliveryService = false;
          }
          if(this.location.skipOrderTypeSelection === undefined || this.location.skipOrderTypeSelection === null){
            this.location.skipOrderTypeSelection = false;
          }
          if(this.location.presentCounter === undefined || this.location.presentCounter === null){
            //Wrap the present-counter settings in an object; migrate the legacy flat fields if present.
            this.location.presentCounter = {
              mode: this.location.presentCounterMode || "TERMINAL",   // absent = terminal-based
              startCounter: (this.location.startCounter !== undefined && this.location.startCounter !== null) ? this.location.startCounter : 1,
              maxCounter: (this.location.maxCounter !== undefined && this.location.maxCounter !== null) ? this.location.maxCounter : 0   // 0 = no upper bound
            };
          }
          if(this.location.contactPersons === undefined || this.location.contactPersons === null){
            this.location.contactPersons = [];
          }
          if(this.location.useManualPickupOrDeliveryTime === undefined || this.location.useManualPickupOrDeliveryTime === null){
            this.location.useManualPickupOrDeliveryTime = false;
          }
          if(this.location.showPickupTimeOptions === undefined || this.location.showPickupTimeOptions === null){
            this.location.showPickupTimeOptions = false;
          }
          if(!this.location.kioskSetting){
            this.location.kioskSetting = {};
          }
          if(!this.location.kioskSetting.dineInLabel){
            this.location.kioskSetting.dineInLabel = '';
          }
          if(!this.location.kioskSetting.pickupLabel){
            this.location.kioskSetting.pickupLabel = '';
          }
          if(!this.location.kioskSetting.colorButton){
            this.location.kioskSetting.colorButton = '';
          }
          if(!this.location.kioskSetting.colorButtonPressed){
            this.location.kioskSetting.colorButtonPressed = '';
          }
          if(!this.location.kioskSetting.colorButtonDisabled){
            this.location.kioskSetting.colorButtonDisabled = '';
          }
          if(!this.location.kioskSetting.colorCategoriesPanelBg){
            this.location.kioskSetting.colorCategoriesPanelBg = '';
          }
          if(!this.location.kioskSetting.colorCategoryCellBg){
            this.location.kioskSetting.colorCategoryCellBg = '';
          }
          if(!this.location.kioskSetting.colorCategoryCellSelectedBg){
            this.location.kioskSetting.colorCategoryCellSelectedBg = '';
          }
          if(!this.location.kioskSetting.colorCategoryTitleText){
            this.location.kioskSetting.colorCategoryTitleText = '';
          }
          if(!this.location.kioskSetting.colorItemsPanelBg){
            this.location.kioskSetting.colorItemsPanelBg = '';
          }
          if(!this.location.kioskSetting.colorItemCellBg){
            this.location.kioskSetting.colorItemCellBg = '';
          }
          if(!this.location.kioskSetting.colorItemNameText){
            this.location.kioskSetting.colorItemNameText = '';
          }
          if(!this.location.kioskSetting.colorItemDescriptionText){
            this.location.kioskSetting.colorItemDescriptionText = '';
          }
          if(!this.location.kioskSetting.colorItemPriceText){
            this.location.kioskSetting.colorItemPriceText = '';
          }
          if(this.location.kioskSetting.showHeader === undefined || this.location.kioskSetting.showHeader === null){
            this.location.kioskSetting.showHeader = true;
          }
          if(!this.location.kioskSetting.startPageButtonSize){
            this.location.kioskSetting.startPageButtonSize = 'MEDIUM';
          }
          if(!this.location.kioskSetting.serviceOptions){
            this.location.kioskSetting.serviceOptions = [];
          }
          if(!this.location.posSetting){
            this.location.posSetting = {};
          }
          if(!this.location.posSetting.serviceOptions){
            this.location.posSetting.serviceOptions = [];
          }
          if(!this.location.posSetting.colorButton){
            this.location.posSetting.colorButton = '';
          }
          if(!this.location.posSetting.colorButtonPressed){
            this.location.posSetting.colorButtonPressed = '';
          }
          if(!this.location.posSetting.colorButtonDisabled){
            this.location.posSetting.colorButtonDisabled = '';
          }
          //Every location onboarded before 2026-10-03 has no kdsSetting at all
          if(!this.location.kdsSetting){
            this.location.kdsSetting = {};
          }
          for (const field of KDS_COLOR_FIELDS) {
            if(!this.location.kdsSetting[field]){
              this.location.kdsSetting[field] = '';
            }
          }
          for (const field of KDS_MINUTE_FIELDS) {
            if(this.location.kdsSetting[field.key] === undefined || this.location.kdsSetting[field.key] === null){
              this.location.kdsSetting[field.key] = '';
            }
          }
          //Absent means the classic card — every location onboarded before 2026-10-03
          this.location.kdsSetting.kitchenCard = this.location.kdsSetting.kitchenCard === true;
          //Sort order: absent means the order tickets came in
          this.location.kdsSetting.sortBy = KDS_SORT_ORDERS.includes(this.location.kdsSetting.sortBy) ? this.location.kdsSetting.sortBy : 'ORDERED';
          //Stations: absent means none — every screen shows every line
          this.location.kdsSetting.stations = (Array.isArray(this.location.kdsSetting.stations) ? this.location.kdsSetting.stations : [])
            .filter(station => station && station.id)
            .map(station => ({ id: station.id, name: station.name || '', categoryIds: Array.isArray(station.categoryIds) ? station.categoryIds : [] }));
          this.location.kdsSetting.useExpo = this.location.kdsSetting.useExpo === true;
          this.location.kdsSetting.autoFoodReadySms = this.location.kdsSetting.autoFoodReadySms === true;
          this.loadKdsStationCategories();
          this.loading = false;
        },
        (error) => {
          console.log(error);
        }
    );
  },
  computed: {
    activateUploadHeaderFileButton(){
      if(this.location.headerImageFile !== ''){
        return true;
      } else {
        return false;
      }
    },
    activateSaveButton(){
      if(this.hasInvalidColors) return false;
      if(this.hasInvalidKdsMinutes) return false;
      if(this.hasUnnamedKdsStation) return false;
      if(this.location.name.length > 0 && this.location.shortName.length > 0 && this.location.vatNumber.length > 0 && this.location.street.length > 0 && this.location.zipCode > 0
          && this.location.city.length > 0 && this.location.phone > 0 && this.validEmail){

        return true;
      } else {
        return false;
      }
    },
    hasUnnamedKdsStation(){
      return (this.location.kdsSetting.stations || []).some(station => !station.name.trim());
    },
    hasInvalidKdsMinutes(){
      return KDS_MINUTE_FIELDS.some(field => !this.isValidMinutes(this.location.kdsSetting[field.key]));
    },
    hasInvalidColors(){
      const kioskFields = [
        'colorButton', 'colorButtonPressed', 'colorButtonDisabled',
        'colorCategoriesPanelBg', 'colorCategoryCellBg', 'colorCategoryCellSelectedBg', 'colorCategoryTitleText',
        'colorItemsPanelBg', 'colorItemCellBg', 'colorItemNameText', 'colorItemDescriptionText', 'colorItemPriceText'
      ];
      const posFields = ['colorButton', 'colorButtonPressed', 'colorButtonDisabled'];
      for (const field of kioskFields) {
        if (!this.isValidColor(this.location.kioskSetting[field])) return true;
      }
      for (const field of posFields) {
        if (!this.isValidColor(this.location.posSetting[field])) return true;
      }
      for (const field of KDS_COLOR_FIELDS) {
        if (!this.isValidColor(this.location.kdsSetting[field])) return true;
      }
      return false;
    },
    serviceOptionDineIn: {
      get() {
        return this.location.kioskSetting.serviceOptions.includes('DINE_IN');
      },
      set(val) {
        if (val) {
          if (!this.location.kioskSetting.serviceOptions.includes('DINE_IN')) {
            this.location.kioskSetting.serviceOptions.push('DINE_IN');
          }
        } else {
          this.location.kioskSetting.serviceOptions = this.location.kioskSetting.serviceOptions.filter(o => o !== 'DINE_IN');
        }
      }
    },
    serviceOptionPickup: {
      get() {
        return this.location.kioskSetting.serviceOptions.includes('PICKUP');
      },
      set(val) {
        if (val) {
          if (!this.location.kioskSetting.serviceOptions.includes('PICKUP')) {
            this.location.kioskSetting.serviceOptions.push('PICKUP');
          }
        } else {
          this.location.kioskSetting.serviceOptions = this.location.kioskSetting.serviceOptions.filter(o => o !== 'PICKUP');
        }
      }
    },
    posOrderTypeDineIn: {
      get() {
        return this.location.posSetting.serviceOptions.includes('DINE_IN');
      },
      set(val) {
        if (val) {
          if (!this.location.posSetting.serviceOptions.includes('DINE_IN')) {
            this.location.posSetting.serviceOptions.push('DINE_IN');
          }
        } else {
          this.location.posSetting.serviceOptions = this.location.posSetting.serviceOptions.filter(o => o !== 'DINE_IN');
        }
      }
    },
    posOrderTypePickup: {
      get() {
        return this.location.posSetting.serviceOptions.includes('PICKUP');
      },
      set(val) {
        if (val) {
          if (!this.location.posSetting.serviceOptions.includes('PICKUP')) {
            this.location.posSetting.serviceOptions.push('PICKUP');
          }
        } else {
          this.location.posSetting.serviceOptions = this.location.posSetting.serviceOptions.filter(o => o !== 'PICKUP');
        }
      }
    }
  },
  methods: {
    /** Empty (the KDS's default) or a whole number of minutes, 1–MAX_KDS_MINUTES. */
    isValidMinutes(value){
      if (value === '' || value === null || value === undefined) return true;
      return Number.isInteger(value) && value >= 1 && value <= MAX_KDS_MINUTES;
    },
    isValidColor(value){
      if (!value || value === '') return true;
      return /^#[0-9A-Fa-f]{6}$/.test(value);
    },
    async confirmResetPresentCounter(){
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Nullstill bestillingsnr',
        message: 'Nullstille den løpende bestillingsnr-serien for denne lokasjonen nå? Neste bestilling starter på nytt (samme som ved dagens oppgjør).',
        okButton: 'Nullstill', cancelButton: 'Avbryt'
      });
      if(!ok) return;
      this.resetMessage = '';
      this.resetError = false;
      LocationService.resetPresentCounter(this.location.merchant, this.locationId).then(
        () => { this.resetError = false; this.resetMessage = 'Nullstilt ✓'; },
        (error) => { console.log(error); this.resetError = true; this.resetMessage = 'Kunne ikke nullstille.'; }
      );
    },
    isSuperAdmin(){
      return UserUtil.isSuperAdmin();
    },
    addContactPerson(){
      if(!this.location.contactPersons){
        this.location.contactPersons = [];
      }
      this.location.contactPersons.push({ name: "", phone: "", role: "" });
    },
    /** A station's id is what each kitchen display remembers it by — never reused, never edited. */
    addKdsStation(){
      const id = 'st' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      this.location.kdsSetting.stations.push({ id: id, name: '', categoryIds: [] });
    },
    async removeKdsStation(index){
      const station = this.location.kdsSetting.stations[index];
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Fjern stasjon',
        message: 'Fjerne «' + (station.name.trim() || 'stasjonen') + '»? Kjøkkenskjermer som er satt opp som denne stasjonen viser alle linjer igjen. Endringen lagres når du trykker Lagre.',
        okButton: 'Fjern', cancelButton: 'Avbryt'
      });
      if(!ok) return;
      this.location.kdsSetting.stations.splice(index, 1);
    },
    /** The categories a station can take: those sold at this location, without the delivery fee. */
    loadKdsStationCategories(){
      ProductService.getCategories().then(
        (response) => {
          this.kdsStationCategories = (response.data || []).filter(category =>
            category.itemType !== 'DELIVERY_SERVICE'
            && (!Array.isArray(category.availableAtLocationIds) || category.availableAtLocationIds.length === 0
              || category.availableAtLocationIds.includes(this.locationId)));
        },
        (error) => { console.log(error); }
      );
    },
    removeContactPerson(index){
      this.location.contactPersons.splice(index, 1);
    },
    validateEmail(){
        var re = /\S+@\S+\.\S+/;
        let validatedResult = re.test(this.location.email);
        this.validEmail = validatedResult;
    },
    selectHeaderFile(){
      this.location.headerImageFile = this.$refs.headerFile.files[0];
      console.log("headerImageFile: " + this.location.headerImageFile.name)
    },
    uploadHeaderFile(){
      this.loading = true;
      MediaFileService.uploadImageFile(this.location.headerImageFile).then(
          (response) => {
            let imageUrl = response.data;
            if(imageUrl){
              console.log("headerImageFile: " + imageUrl);
              this.location.headerImageFile = imageUrl;
            } else {
              console.log("Uploading headerImageFile failed");
            }
            this.loading = false;
          },
          (error) => {
            console.log(error);
          }
      );
    },
    saveLocation(){
      this.loading = true;

      // Remove empty color properties instead of saving empty strings
      const kioskColorFields = [
        'colorButton', 'colorButtonPressed', 'colorButtonDisabled',
        'colorCategoriesPanelBg', 'colorCategoryCellBg', 'colorCategoryCellSelectedBg', 'colorCategoryTitleText',
        'colorItemsPanelBg', 'colorItemCellBg', 'colorItemNameText', 'colorItemDescriptionText', 'colorItemPriceText'
      ];
      const posColorFields = ['colorButton', 'colorButtonPressed', 'colorButtonDisabled'];
      for (const field of kioskColorFields) {
        if (!this.location.kioskSetting[field]) {
          delete this.location.kioskSetting[field];
        }
      }
      for (const field of posColorFields) {
        if (!this.location.posSetting[field]) {
          delete this.location.posSetting[field];
        }
      }

      // The online shop's header shows displayName with no fallback, so an empty one would leave it blank:
      // empty is saved as the name, as the hint under the field says.
      this.location.displayName = (this.location.displayName || '').trim() || this.location.name;
      this.location.locationId = this.locationId;
      this.location.zipCode = this.location.zipCode.toString();
      this.location.phone = this.location.phone.toString();
      this.location.updatedDateTime = (new Date()).toISOString();
      this.editLocation({ ...this.location, kdsSetting: this.kdsSettingToSave() });
    },
    // The KDS's defaults are stored as absent — the KDS reads absent as today's screen — but stripped from a COPY.
    // The form stays bound to location.kdsSetting after a save, and with its stations deleted the next «Lagre»
    // threw on stations.forEach and the stations section on stations.length.
    kdsSettingToSave(){
      const kdsSetting = JSON.parse(JSON.stringify(this.location.kdsSetting));
      for (const field of KDS_COLOR_FIELDS) {
        if (!kdsSetting[field]) {
          delete kdsSetting[field];
        }
      }
      for (const field of KDS_MINUTE_FIELDS) {
        if (kdsSetting[field.key] === '' || !this.isValidMinutes(kdsSetting[field.key])) {
          delete kdsSetting[field.key];
        }
      }
      // Off is stored as absent, the same as every location that never had it — the KDS reads both as the classic card.
      if (kdsSetting.kitchenCard !== true) {
        delete kdsSetting.kitchenCard;
      }
      // Dark screen and text size moved to each screen (TastyKDS, 2026-10-09), and the KDS no longer reads them here:
      // a value saved before goes with the next save.
      delete kdsSetting.darkTheme;
      delete kdsSetting.textSize;
      if (kdsSetting.sortBy === 'ORDERED' || !KDS_SORT_ORDERS.includes(kdsSetting.sortBy)) {
        delete kdsSetting.sortBy;
      }
      // No stations is stored as absent: every screen shows every line, as before stations existed.
      const stations = (kdsSetting.stations || []).map(station => ({ ...station, name: station.name.trim() }));
      if (stations.length > 0) {
        kdsSetting.stations = stations;
      } else {
        delete kdsSetting.stations;
      }
      if (kdsSetting.useExpo !== true || !kdsSetting.stations) {
        delete kdsSetting.useExpo;
      }
      if (kdsSetting.autoFoodReadySms !== true) {
        delete kdsSetting.autoFoodReadySms;
      }
      return kdsSetting;
    },
    editLocation(location){
      LocationService.editLocation(location).then(
          (response) => {
            let documentId = response.data;
            if(documentId){
              this.showToast = true;
              setTimeout(() => { this.showToast = false; }, 3000);
            } else {
              console.log("Editing location failed");
            }
            this.loading = false;
          },
          (error) => {
            console.log(error);
          }
      );
    }
  }
};
</script>

<style scoped>
.section {
  background-color: var(--bo-surface-2);
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  padding: 20px;
}
.section-title {
  margin-top: 0;
  margin-bottom: 12px;
}
.subsection {
  background-color: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-radius: 4px;
  padding: 16px;
}
.subsection-title {
  margin-top: 0;
  margin-bottom: 4px;
}
.toast-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.toast-message {
  background-color: rgba(40, 167, 69, 0.85);
  color: #fff;
  padding: 16px 32px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 1.1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
.sticky-footer {
  position: sticky;
  bottom: 0;
  background-color: var(--bo-surface-2);
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 15px 20px;
  z-index: 10;
}
</style>
